'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId || measurementId === 'your-google-analytics-id-here') return;

    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = [];
      window.gtag = function (...args: any[]) {
        window.dataLayer.push(args);
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, { send_page_view: false });
    }

    const url = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: url,
        page_title: document.title,
      });
    }
  }, [pathname, searchParams]);
}

export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventParams);
  }
}

// Marketplace-specific tracking helpers
export function trackTemplateView(templateId: string, templateName: string, platform: string, price: number) {
  trackEvent('view_item', {
    item_id: templateId,
    item_name: templateName,
    item_category: platform,
    price,
  });
}

export function trackBuyTemplateClick(templateId: string, templateName: string, platform: string, price: number) {
  trackEvent('add_to_cart', {
    item_id: templateId,
    item_name: templateName,
    item_category: platform,
    price,
    currency: 'USD',
  });
}

export function trackCheckoutStart(templateId: string, templateName: string, price: number) {
  trackEvent('begin_checkout', {
    item_id: templateId,
    item_name: templateName,
    value: price,
    currency: 'USD',
  });
}

export function trackCheckoutSuccess(templateId: string, templateName: string, price: number, orderId?: string) {
  trackEvent('purchase', {
    transaction_id: orderId || templateId,
    item_id: templateId,
    item_name: templateName,
    value: price,
    currency: 'USD',
  });
}

export function trackCheckoutAbandonment(templateId: string, templateName: string, step: string) {
  trackEvent('checkout_abandonment', {
    item_id: templateId,
    item_name: templateName,
    step,
  });
}

export function trackPlatformFilter(platform: string) {
  trackEvent('platform_filter', {
    platform,
  });
}

export function trackSearchQuery(query: string, resultsCount: number) {
  trackEvent('search', {
    search_term: query,
    results_count: resultsCount,
  });
}

export function trackRelatedTemplateClick(templateId: string, templateName: string, sourceTemplateId: string) {
  trackEvent('related_template_click', {
    item_id: templateId,
    item_name: templateName,
    source_template_id: sourceTemplateId,
  });
}

// ============================================================
// Enhanced Checkout Analytics
// ============================================================

export function trackCheckoutStarted(items: { id: string; name: string; price: number; platform: string }[], totalValue: number) {
  trackEvent('checkout_started', {
    currency: 'USD',
    value: totalValue,
    items: items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.platform,
      price: item.price,
    })),
  });
}

export function trackCheckoutStep(step: string, stepIndex: number, items: { id: string; name: string; price: number }[]) {
  trackEvent('checkout_progress', {
    checkout_step: stepIndex,
    checkout_option: step,
    items: items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      price: item.price,
    })),
  });
}

export function trackCheckoutAbandoned(step: string, items: { id: string; name: string; price: number }[], timeSpentSeconds: number) {
  trackEvent('checkout_abandoned', {
    abandoned_step: step,
    time_spent_seconds: timeSpentSeconds,
    items: items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      price: item.price,
    })),
  });
}

export function trackCheckoutCompleted(
  orderId: string,
  items: { id: string; name: string; price: number; platform: string }[],
  totalValue: number,
  paymentMethod: string,
  timeSpentSeconds: number
) {
  trackEvent('purchase', {
    transaction_id: orderId,
    currency: 'USD',
    value: totalValue,
    payment_type: paymentMethod,
    time_to_purchase_seconds: timeSpentSeconds,
    items: items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.platform,
      price: item.price,
    })),
  });
}

export function trackPaymentMethodSelected(method: string) {
  trackEvent('payment_method_selected', { payment_method: method });
}

export function trackWishlistAdd(templateId: string, templateName: string, platform: string, price: number) {
  trackEvent('add_to_wishlist', {
    item_id: templateId,
    item_name: templateName,
    item_category: platform,
    price,
    currency: 'USD',
  });
}

export function trackWishlistRemove(templateId: string, templateName: string) {
  trackEvent('remove_from_wishlist', {
    item_id: templateId,
    item_name: templateName,
  });
}

export function trackSearchSuggestionClick(templateId: string, templateName: string, query: string) {
  trackEvent('search_suggestion_click', {
    item_id: templateId,
    item_name: templateName,
    search_term: query,
  });
}
